<template>
  <b-row :id="filterRowId" :class="wrapperClass" @clear-filter="clearFilter">
    <b-form-group
      :disabled="disable"
      :aria-disabled="disable"
      :id="filterLabelId"
      :label="labelIf"
      :label-for="filterId"
      :class="labelClass"
    >
      <template #default>
        <div class="raftjs-hovertip" v-if="hasHovertip">
          <img
            v-if="hasHovertip"
            :id="getHovertipId"
            :src="hovertipSrc"
            alt="help"
            style="height: 0.75rem; padding-left: 0.5rem"
          />
          <b-popover
            v-if="hasHovertip"
            :target="getHovertipId"
            triggers="hover focus"
          >
            <template #title>
              {{ getHovertipTitle }}
            </template>
            {{ getHovertipText }}
          </b-popover>
        </div>
      </template>
    </b-form-group>
    <div class="flex-row">
      <multi-select
        v-if="!hideEmpty || getValue?.length"
        class="mr-sm-5 width-constraint"
        :show-labels="false"
        :id="filterId"
        :class="filterClass"
        :disabled="disable"
        :aria-disabled="disable"
        :value="bindValue"
        :options="getValue || []"
        :options-limit="optionsLimit"
        :custom-label="getLabel"
        :multiple="multiple"
        @input="updateFilter"
        placeholder=""
        :required="isRequired"
        :track-by="trackBy"
        :allow-empty="!isRequired"
      />
      <filter-clear-button
        :id="filterId + '-clear-button'"
        v-if="showClearButton"
        @click="clearFilter"
      />
    </div>
  </b-row>
</template>

<script>
import Multiselect from "vue-multiselect";
import PathMapper from "../../lib/PathMapper";
import jsonApiFilterManager from "../../lib/JsonApiFilterManager";
import FilterClearButton from "./FilterClearButton.vue";
import helpOutline from "../raft/help-Outline.svg";

require("vue-multiselect/dist/vue-multiselect.min.css");

export default {
  name: "MultiSelectFilter",
  components: {
    "multi-select": Multiselect,
    FilterClearButton,
  },
  emits: ["update-filter"],
  props: {
    id: { type: String, default: "" },
    label: { type: String, default: "" },
    updateMethod: { type: Function, default: null },
    selectedItems: { type: [Object, Array], default: () => {} },
    getValue: { type: [Object, Array], default: () => {} },
    currentSetValue: { type: [Object, Array], default: () => {} },
    trackBy: { type: String, default: "" },
    disable: Boolean,
    multiple: { type: Boolean, default: true },
    optionsLimit: { type: Number, default: 1000 },
    filterAction: { type: String, default: "" },
    filterKey: { type: String, default: "" },
    hideEmpty: { type: Boolean, default: false },
    displayValue: { type: String, default: "" },
    activeStatus: { type: String, default: "" },
    inactiveText: { type: String, default: "(inactive)" },
    filterClass: { type: [Array, String], default: () => [] },
    storeWatch: { type: String, default: "" },
    storeCommit: { type: String, default: "" },
    path: { type: String, default: "" },
    operator: { type: String, default: "" },
    isRequired: Boolean,
    hovertip: { type: Object, default: () => {} },
    useJsonApiFilterManager: { type: Boolean, default: true },
    wrapperClass: { type: String, default: null },
  },
  data() {
    return {
      hovertipSrc: helpOutline,
      selected: [],
      unwatchHooks: [],
    };
  },
  methods: {
    clearFilter() {
      this.updateFilter([]);
    },
    updateFilter(value) {
      if (this.storeCommit && this.storeCommit.length > 1) {
        this.$store.commit(this.storeCommit, value);
      } else {
        if (this.updateMethod) {
          this.updateMethod(value, this.path);
        }
        this.selected = value;
      }
      this.$emit("update-filter", {
        id: this.id,
        path: this.path,
        operator: this.operator,
        useJsonApiFilterManager: this.useJsonApiFilterManager,
        value,
        filterAction: this.filterAction,
        filterKey: this.filterKey,
        trackBy: this.trackBy,
        itemsSelected: value,
      });
    },
    getLabel: function (option) {
      if (typeof option === "string") {
        return option;
      }
      let label = option[this.displayValue];
      // Status values are often strings like "0".
      if (this.activeStatus && !Number(option[this.activeStatus])) {
        label += " " + this.inactiveText;
      }
      return label;
    },
  },
  computed: {
    filterId: function () {
      return this.id;
    },
    filterLabelId: function () {
      return this.id + "-label";
    },
    filterRowId: function () {
      return this.id + "-row-wrapper";
    },
    labelIf: function () {
      if (!this.hideEmpty || this.getValue?.length) {
        return this.label;
      }
      return "";
    },
    labelClass: function () {
      const value = [];
      if (this.disable) {
        value.push("multiselect-label--disabled");
      }
      if (this.isRequired) {
        value.push("form-required multiselect-label-required");
      }
      if (this.hasHovertip) {
        value.push("raftjs-hovertip");
      }
      return value.join(" ");
    },
    getHelpIconPath: function () {
      return PathMapper.getIconAssetPath("help.png", this.$isB4);
    },
    hasHovertip: function () {
      return this.hovertip;
    },
    getHovertipId: function () {
      return this.hovertip.id;
    },
    getHovertipTitle: function () {
      return this.hovertip.title;
    },
    getHovertipText: function () {
      return this.hovertip.text;
    },
    bindValue: function () {
      if (this.$store.getters[this.storeWatch]) {
        return this.$store.getters[this.storeWatch];
      }
      return this.currentSetValue;
    },
    showClearButton() {
      if (this.isRequired) {
        return false;
      }
      if (this.bindValue && !Array.isArray(this.bindValue)) {
        return this.bindValue?.[this.trackBy];
      }
      return this.bindValue?.length > 0;
    },
  },
  mounted() {
    if (this.useJsonApiFilterManager) {
      const unwatch = this.$store.watch(
        (state, getters) => this.$store.getters[this.storeWatch],
        (newValue, oldValue) => {
          if (
            typeof newValue === "undefined" ||
            newValue === null ||
            newValue.length < 0
          ) {
            jsonApiFilterManager.deleteFiltersByName([this.id]);
            return;
          }
          if (
            (typeof newValue === "object" || typeof newValue === "string") &&
            !Array.isArray(newValue)
          ) {
            newValue = [newValue];
          }
          const options = [];
          if (newValue.length < 0) {
            jsonApiFilterManager.deleteFiltersByName([this.id]);
            return;
          }
          newValue?.forEach((val) => {
            options?.push(val[this.trackBy]);
          });
          jsonApiFilterManager.deleteFiltersByName([this.id]);
          if (options?.length > 0 && this.path && !this.filterAction) {
            jsonApiFilterManager.addMultivalueNamedFilter(
              this.id,
              this.path,
              options,
              this.storeCommit,
              newValue,
            );
          }
        },
      );
      this.unwatchHooks.push(unwatch);
    }
  },
  destroyed() {
    for (const hook of this.unwatchHooks) {
      hook();
    }
  },
};
</script>

<style scoped>
:is(#raft-js, #raft-js-app) div.row >>> .multiselect {
  margin: 0.125rem 0 0 0;
}

:is(#raft-js, #raft-js-app) .flex-row {
  display: flex;
  align-items: center;
}

#bio-project-materials .flex-row {
  display: flex;
  align-items: center;
}

.width-constraint {
  width: 90%;
}
</style>
